import { Button, ButtonGroup, FormControl, InputLabel, Menu, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { appendToCallItemNotes, selectCallItem, setIsRescheduleModalOpen, setPropertiesOnSelectedCallItem } from '../../../store/officeScheduler.slice';
import phoneFormat from '../../../utils/phoneFormat';
import { OfficeSchedulerModeEnum } from '../../Calendar/Enums/OfficeSchedulerModeEnum';
import { formatDate } from '../../../utils/dateHelpers';
import { toTitleCase } from '../../../utils/common';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChangeStatusModal from '../../OnboardingWorkflow/Modals/ChangeStatusModal';
import DemographicsRequest from '../../OnboardingWorkflow/DemographicsRequest/DemographicsRequest';
import CallItem from '../../../Models/OnboardingWorkflow/CallItem.model';
import {
    openChangeStatusHandler,
    setDemographicsRequestIsOpen
} from '../../../store/onboardingWorkflow.slice';
import SchedulingAddPhoneModal from './SchedulingAddPhoneModal';

interface SchedulingButtonsPaneProps {
    setOpenEditor?: any;
    mode: OfficeSchedulerModeEnum;
    refreshMemberData?: () => Promise<void>;
}

const SchedulingButtonsPane: React.FC<SchedulingButtonsPaneProps> = ({ setOpenEditor, mode, refreshMemberData }: SchedulingButtonsPaneProps) => {
    const dispatch = useAppDispatch();
    const { selectedCallItem, member, associatedEvent } = useSelector((state: RootState) => state.officeSchedulerSlice);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const { demographicsRequestIsOpen } = useSelector((state: RootState) => state.onboardingWorkflowSlice);
    const [currentMemberPhones, setCurrentMemberPhones] = useState<any>([]);
    const [callNumber, setCallNumber] = useState<string>('');
    const [textable, setTextable] = useState<boolean>(false);
    const [prevDemographicsRequestOpen, setPrevDemographicsRequestOpen] = useState<boolean>(false);
    const [showAddPhoneModal, setShowAddPhoneModal] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const selectPhoneHandler = (phone: any) => {
        setCallNumber(phone.number);
        if (differenceInDays >= 3) {
            setTextable(phone.textable);
        } else {
            setTextable(false);
        }
        handleClose();
    };

    const today = new Date(new Date().setHours(0, 0, 0, 0));
    const apptDate = new Date(new Date(selectedCallItem.AppointmentDateTime).setHours(0, 0, 0, 0));
    const differenceInTime = apptDate.getTime() - today.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);
    const providerNames = useMemo(() => {
        return associatedEvent.providerNames.join(', ');
    }, [associatedEvent]);
    const memberName = useMemo(() => toTitleCase(member.FirstName), [member]);
    const eventTime = useMemo(() => formatDate(new Date(associatedEvent?.AppointmentDateTime ?? new Date()), 'MM/dd/yyyy h:mm aa'), [associatedEvent]);

    useEffect(() => {
        if (member?.Phones) {
            const sortedPhones = structuredClone(member.Phones).sort(function (x, y) {
                // true values first
                return x.IsPrimary === y.IsPrimary ? 0 : x.IsPrimary ? -1 : 1;
            });
            const phoneList = sortedPhones.map((p) => {
                let display = phoneFormat(p.Number);
                let number = p.Number.replace(/\D/g, '');
                let textable = p.Textable;
                if (p.IsPrimary) {
                    display = `Primary: ${phoneFormat(p.Number)}`;
                }

                if (p.Textable) {
                    display = display + '  - textable';
                }
                return { display: display, number: number, textable: textable };
            });
            setCurrentMemberPhones(phoneList);
        } else {
            setCurrentMemberPhones([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [member]);

    useEffect(() => {
        if (currentMemberPhones.length > 0) {
            setCallNumber(currentMemberPhones[0].number);
            if (differenceInDays >= 3) {
                setTextable(currentMemberPhones[0].textable);
            } else {
                setTextable(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMemberPhones]);

    function handleNumberChange(event) {
        setCallNumber(event.target.value);
        const currentPhone = currentMemberPhones.find((p) => p.number === event.target.value);

        if (differenceInDays >= 3) {
            setTextable(currentPhone.textable);
        } else {
            setTextable(false);
        }
    }

    const handleCallClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallAttempted: true, LastContact: new Date(), LastCalledNumber: callNumber }));
        dispatch(appendToCallItemNotes(`Attempted to call ${memberName} at ${phoneFormat(callNumber)}`));
    };

    const handleConfirmClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallStatus: 'Confirmed', LastContact: new Date() }));
        dispatch(appendToCallItemNotes(`Confirmed ${memberName}'s ${eventTime} appointment with ${providerNames}.`));
    };

    const handleCancelledClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallStatus: 'Cancelled' }));
        dispatch(appendToCallItemNotes(`${memberName} cancelled the ${eventTime} appointment with ${providerNames}.`));
    };

    const handleNotReachedClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallStatus: 'NotReached' }));
        dispatch(appendToCallItemNotes(`Unable to reach ${memberName} to confirm the ${eventTime} appointment with ${providerNames}.`));
    };

    const handleSkippedClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallStatus: 'Skipped' }));
        dispatch(
            appendToCallItemNotes(
                `Skipped confirmation of ${memberName}'s ${eventTime} appointment with ${providerNames} skipped due to no phone number provided.`
            )
        );
    };

    const handleRescheduleClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallStatus: 'ConfirmedByCalendar', LastContact: new Date() }));
        dispatch(appendToCallItemNotes(`Rescheduled ${memberName}'s ${eventTime} appointment with ${providerNames}.`));
        dispatch(setIsRescheduleModalOpen(true));
    };

    const handleNotRescheduleClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallStatus: 'Not Rescheduled' }));
        dispatch(appendToCallItemNotes(`Attempted to call ${member?.LastName}, ${member?.FirstName} at ${phoneFormat(callNumber)}`));
    };

    const handleTxtClick = () => {
        dispatch(setPropertiesOnSelectedCallItem({ CallStatus: 'Text Sent', LastContact: new Date() }));
        const provider = activeProviders.find((p) => p.Id === associatedEvent.ProviderIds[0]);
        const providerRole = provider.Role ?? '';
        const providerName = provider.FullName;
        navigator.clipboard
            .writeText(
                `Hello, thank you for being our member at Catalytic Health Partners! ` +
                `We look forward to assisting you. This is a reminder of your upcoming appointment with your ` +
                `(${providerRole}) ${providerName}, ` +
                `on ${associatedEvent.AppointmentDateTime.toLocaleDateString()}. Please confirm this appointment by texting back Yes`
            )
            .then(
                function () {
                    console.log('Async: Copying to clipboard was successful!');
                },
                function (err) {
                    console.error('Async: Could not copy text: ', err);
                }
            );
    };

    // Handlers for new buttons
    const openAddPhoneNumberHandler = () => {
        setShowAddPhoneModal(true);
    };

    const handlePhoneAdded = async () => {
        // After phone is added, refresh member data by re-selecting the current call item
        setShowAddPhoneModal(false);
        if (selectedCallItem) {
            // Force refresh by temporarily deselecting and reselecting
            dispatch(selectCallItem(null));
            // Use setTimeout to ensure the state update completes before reselecting
            setTimeout(() => {
                dispatch(selectCallItem(selectedCallItem));
            }, 0);
        }
    };

    const openDemographicsRequest = () => {
        dispatch(setDemographicsRequestIsOpen(true));
    };

    const openChangeStatus = () => {
        const callItemForModal: Partial<CallItem> = {
            MemberId: member?.Id,
            FirstName: member?.FirstName,
            LastName: member?.LastName,
            MemberStatus: typeof member?.Status === 'string' ? parseInt(member.Status) : member?.Status,
            PopulationName: selectedCallItem?.PopulationName || '',
            PopulationNumber: member?.Population || null,
        };
        dispatch(openChangeStatusHandler(callItemForModal as CallItem));
    };

    useEffect(() => {
        if (prevDemographicsRequestOpen && !demographicsRequestIsOpen) {
            if (selectedCallItem?.Id) {
                dispatch(appendToCallItemNotes(`Demographics request sent for ${memberName}.`));
            }
        }
        setPrevDemographicsRequestOpen(demographicsRequestIsOpen);
    }, [demographicsRequestIsOpen, prevDemographicsRequestOpen, selectedCallItem?.Id, memberName, dispatch]);

    return (
        <>
            <SchedulingAddPhoneModal 
                open={showAddPhoneModal} 
                onClose={() => setShowAddPhoneModal(false)}
                memberId={member?.Id}
                currentPhones={member?.Phones || []}
                onPhoneAdded={handlePhoneAdded}
            />
            <ChangeStatusModal />
            <DemographicsRequest lockedMemberOption={{ MemberId: member?.Id, MemberName: `${member?.LastName}, ${member?.FirstName}` }} />

            <Stack spacing={2} marginTop={2} marginBottom={2}>
                <Stack direction="row" spacing={2}>
                    <ButtonGroup>
                        <Button
                            disabled={!member?.Phones}
                            variant="contained"
                            sx={{ '& a': { color: '#fff', textDecoration: 'none' } }}
                            onClick={handleCallClick}
                        >
                            <a href={`tel:+1${callNumber}`}>Make a Call</a>
                        </Button>
                        <Button
                            disabled={!member?.Phones || currentMemberPhones.length === 0}
                            id="phone-select-button"
                            variant="contained"
                            aria-controls={open ? 'phone-select-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                        >
                            <KeyboardArrowDownIcon />
                        </Button>
                        <Menu
                            id="phone-select-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                'aria-labelledby': 'phone-select-button',
                            }}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{
                                [`& .MuiMenuItem-root.Mui-selected`]: {
                                    backgroundColor: 'primary.main',
                                    color: '#F7F7F7',
                                    ':hover': {
                                        backgroundColor: 'primary.main',
                                        color: '#F7F7F7',
                                    },
                                },
                            }}
                        >
                            {currentMemberPhones.map((phone: any, index: number) => {
                                const isSelected = callNumber === phone.number;
                                const iconColor = isSelected ? '#fff' : '#1976d2';
                                return (
                                    <MenuItem key={index} selected={isSelected} onClick={() => selectPhoneHandler(phone)}>
                                        {phone.display}
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </ButtonGroup>

                    <Button disabled={!member?.Id} variant="outlined" color="primary" onClick={openAddPhoneNumberHandler}>
                        Add Phone
                    </Button>

                    <Button variant="contained" color="primary" onClick={openDemographicsRequest}>
                        Demographics Request
                    </Button>
                </Stack>

                <Stack direction="row" spacing={2}>
                    <Button variant="contained" color="secondary" onClick={openChangeStatus}>
                        Change Status
                    </Button>

                    {mode === OfficeSchedulerModeEnum.Confirmations && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!selectedCallItem.CallAttempted}
                            onClick={handleConfirmClick}
                        >
                            Confirm
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!selectedCallItem.CallAttempted}
                        onClick={handleNotReachedClick}
                    >
                        Not Reached
                    </Button>

                    {mode === OfficeSchedulerModeEnum.Confirmations && (
                        <Button
                            variant="contained"
                            color="error"
                            disabled={!selectedCallItem.CallAttempted}
                            onClick={handleCancelledClick}
                        >
                            Cancel
                        </Button>
                    )}

                    {mode === OfficeSchedulerModeEnum.Rescheduling && (
                        <Button
                            variant="contained"
                            color="error"
                            disabled={!selectedCallItem.CallAttempted}
                            onClick={handleNotRescheduleClick}
                        >
                            Do Not Reschedule
                        </Button>
                    )}

                    {(!member?.Phones || member?.Phones?.length === 0) && (
                        <Button variant="contained" color="warning" onClick={handleSkippedClick}>
                            Skip
                        </Button>
                    )}

                    {mode === OfficeSchedulerModeEnum.Confirmations && textable && (
                        <Button variant="contained" color="primary" onClick={handleTxtClick}>
                            Copy Text
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!selectedCallItem.CallAttempted}
                        onClick={handleRescheduleClick}
                    >
                        Reschedule
                    </Button>
                </Stack>
            </Stack>
        </>
    );
};

export default SchedulingButtonsPane;
